import type { Core } from '@pdftron/webviewer';
import { GlobalVariables } from '../pdf/GlobalVariables';
import { devWarning } from './developmentUtils';
import { ApryseSDK } from '../pdf/ApryseSDK';

export function findCommonProperties(annotations: Core.Annotations.Annotation[]): string[] {
    const supportedProperties = [
        'borderColor',
        'borderStyle',
        'borderWidth',
        'color',
        'fillColor',
        'lineColor',
        'lineEnd',
        'lineStart',
        'lineStyle',
        'lineCloudyStyle',
        'lineWidth',
        'opacity',
        'ratio',
        'rotation',
    ];

    const isSupported = (propertyName: string) => supportedProperties.includes(propertyName);

    interface PropertyCountEntry {
        name: string;
        count: number;
    }

    const propertyCounts: Array<PropertyCountEntry> = [];

    const countProperty = (propertyName: string) => {
        if (!isSupported(propertyName)) {
            devWarning(
                `Unsupported annotation property '${propertyName}': there was no corresponding handling implemented and therefore it wont be displayed.`,
            );
            return;
        }

        const index = propertyCounts.findIndex(entry => entry.name === propertyName);
        if (index === -1) {
            propertyCounts.push({
                name: propertyName,
                count: 1,
            } as PropertyCountEntry);
        } else {
            propertyCounts[index].count += 1;
        }
    };

    annotations.forEach(annotation => {
        const properties = propertiesOfAnnotation(annotation);
        properties.forEach(property => countProperty(property));
    });

    const allAnnotsHaveThis = (entry: PropertyCountEntry) => entry.count === annotations.length;

    const commonProperties: string[] = [];
    propertyCounts.forEach(property => {
        if (allAnnotsHaveThis(property)) {
            commonProperties.push(property.name);
        }
    });

    return commonProperties;
}

export function propertiesOfAnnotation(annotation: Core.Annotations.Annotation): string[] {
    const annotType: string = ApryseSDK.annotPrettyType(annotation);

    switch (annotType) {
        case 'area':
            return ['opacity', 'lineColor', 'lineStyle', 'lineWidth', 'ratio', 'rotation'];
        case 'callout':
            return ['opacity', 'lineColor', 'fillColor'];
        case 'caret':
        case 'highlight':
        case 'squiggly':
            return ['opacity', 'color', 'rotation'];
        case 'circle':
            return ['opacity', 'lineColor', 'fillColor', 'lineStyle', 'lineWidth', 'rotation'];
        case 'distance':
            return ['opacity', 'lineColor', 'ratio', 'rotation'];
        case 'pencil':
            return ['opacity', 'lineColor', 'lineWidth', 'rotation'];
        case 'line':
        case 'polyline':
            return [
                'opacity',
                'lineColor',
                'lineStart',
                'lineEnd',
                'lineStyle',
                'lineWidth',
                'rotation',
            ];

        case 'perimeter':
            return ['opacity', 'lineColor', 'lineStyle', 'ratio', 'rotation'];
        case 'cloud':
            return [
                'opacity',
                'lineColor',
                'fillColor',
                'lineCloudyStyle',
                'lineWidth',
                'rotation',
            ];
        case 'polygon':
        case 'square':
            return ['opacity', 'lineColor', 'fillColor', 'lineStyle', 'lineWidth', 'rotation'];
        case 'stamp':
            return ['opacity', 'color', 'rotation'];
        case 'textbox':
            return [
                'opacity',
                'borderWidth',
                'borderStyle',
                'borderColor',
                'fillColor',
                // 'rotation',
            ];
        case 'typewriter':
            return [
                'opacity',
                // 'rotation',
            ];
        case 'strikeOut':
        case 'underline':
            return ['opacity', 'lineColor'];
        case 'fileattachment':
        case 'link':
        case 'popup':
        case 'printermark':
        case 'psink':
        case 'redact':
        case 'threed':
        case 'watermark':
            return [];

        // rare unhandled types: movie, screen, sound, trapnet, widget
        default:
            devWarning(
                `Unexpected annotation type '${annotType}': there was no corresponding handling for this.`,
            );
            return [];
    }
}

/**
 * Determines whether annotation can be edited.
 * Returns true if annotation is not readonly, current user is owner and annotation is from the current session.
 */
export function canEditAnnot(annot: Core.Annotations.Annotation): boolean {
    if (annot.ReadOnly || !isOwner(annot)) {
        return false;
    }
    return true;
}

/**
 * Determines whether annotation title equals to current user name.
 */
export function isOwner(annot: Core.Annotations.Annotation): boolean {
    return annot.Author === GlobalVariables.ownersName;
}

/**
 * Determines whether at least one annotation can be edited.
 */
export function canEditAnyAnnot(annots: Core.Annotations.Annotation[]): boolean {
    return annots.some(annot => canEditAnnot(annot));
}
